import axios from 'axios';
import { POLL_REQUEST_TIMEOUT_MS, pollJob } from './job-polling';
import {
  SlideshowFormData,
  SlideshowJobResponse,
  SlideshowJobStatusResponse,
  SlideshowJobProgress
} from '../models/slideshow.types';

const API_BASE_URL = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:6060';

export class SlideshowService {
  /**
   * Start slideshow generation
   */
  static async generateSlideshow(
    formData: SlideshowFormData,
    accessToken: string
  ): Promise<SlideshowJobResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/api/slides/generate`,
      formData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data;
  }

  /**
   * Check slideshow generation status
   */
  static async getJobStatus(
    jobId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<SlideshowJobStatusResponse> {
    const response = await axios.get(
      `${API_BASE_URL}/api/slides/status/${jobId}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        // Without a timeout a hung request stalls the polling loop forever.
        // Generous enough to ride out a Lambda cold start (~8-10s).
        timeout: POLL_REQUEST_TIMEOUT_MS,
        signal
      }
    );
    return response.data;
  }

  /**
   * Poll for job completion.
   *
   * @param signal AbortSignal — callers should abort on unmount so we don't
   *               keep polling for ~10 minutes after the user navigates away.
   *               Mirrors WorksheetService.pollJobStatus so the two can be
   *               driven concurrently off a shared controller.
   */
  static async pollJobStatus(
    jobId: string,
    accessToken: string,
    onProgress?: (progress: SlideshowJobProgress) => void,
    pollInterval: number = 2000,
    maxAttempts: number = 300,  // 10 minutes with 2-second intervals
    signal?: AbortSignal
  ): Promise<SlideshowJobStatusResponse> {
    return pollJob<SlideshowJobStatusResponse>({
      fetchStatus: () => this.getJobStatus(jobId, accessToken, signal),
      isTerminal: (status) => status.status === 'completed' || status.status === 'failed',
      onStatus: (status) => {
        if (onProgress && status.progress) {
          onProgress(status.progress);
        }
      },
      timeoutMessage: 'Slideshow generation timed out. Please try again.',
      pollInterval,
      maxAttempts,
      signal
    });
  }
}